import React, { Component } from 'react';

import FormSettings from '../../../shared/constants/form-settings';
import Defaults from '../../../shared/constants/defaults';

import SettingsTab from '../tab';
import OpenDateSection from '../sections/open-date';
import CloseDateSection from '../sections/close-date';
import LimitSubmissions from '../sections/limitsubmissions';
import FormClosedMessageSection from '../sections/form-closed-message';
import PreOpenMessageSection from '../sections/pre-open-message';

import {coalesce, getUtcString} from '../../../shared/utilities';
import classNames from 'classnames';

const timeInterval = 15;
const timeIntervalMillisecs = 60000 * timeInterval;

export default class SchedulingTab extends SettingsTab {
	constructor (props) {
		super(props);

		const openSchedule = this.getDate(FormSettings.OPEN_SCHEDULE) || getUtcString();
		const minCloseDate = this.getMinCloseDate();
		const closeSchedule = this.getDate(FormSettings.CLOSE_SCHEDULE) ||
			minCloseDate || getUtcString();
		const limitSubmissions = this.coalesceProperty(FormSettings.LIMIT_SUBMISSIONS, null);
		const hasScheduleClosing = !!this.getDate(FormSettings.CLOSE_SCHEDULE);
		const hasScheduleOpening = !!this.getDate(FormSettings.OPEN_SCHEDULE);

		this.state = {
			openSchedule,
			closeSchedule,
			limitSubmissions,
			minCloseDate,
			hasScheduleClosing,
			hasScheduleOpening
		};
	}
	
	render () {
		const {
			openSchedule,
			closeSchedule,
			limitSubmissions,
			hasScheduleOpening,
			hasScheduleClosing,
			minCloseDate
		} = this.state;

		const {
			localization,
			selected
		} = this.props;

		const classes = classNames({
			'hidden': !selected
		}, 'scheduling');

		const openMessage = openSchedule ? (
				<PreOpenMessageSection
						message={this.coalesceProperty(FormSettings.PRE_OPEN_MESSAGE, Defaults.PRE_OPEN_MESSAGE)}
						updateCallback={this.updateSetting}
					/>
			) : null;

		const closeMessage = closeSchedule || limitSubmissions ? (
				<FormClosedMessageSection
						message={this.coalesceProperty(FormSettings.FORM_CLOSED_MESSAGE, Defaults.FORM_CLOSED_MESSAGE)}
						updateCallback={this.updateSetting}
						localization={localization}
					/>
			) : null;
		
		return (
			<section className={classes} key={this.props.customKey}>
				<p className='note'>
					{localization.substitute('Only open forms can accept responses')}
				</p>

				<div className='left column'>
					<h2>{localization.substitute('Open Form')}</h2>
					<OpenDateSection
						schedule={openSchedule}
						hasScheduleOpening={hasScheduleOpening}
						addDateCallback={this.addDate}
						timeInterval={timeInterval}
						removeDateCallback={this.removeDate}
						localization={localization}
					/>
					{openMessage}
				</div>
				<div className='right column'>
					<h2>{localization.substitute('Close Form')}</h2>
					<LimitSubmissions
						submissionMax={this.coalesceProperty(FormSettings.SUBMISSION_MAX, null)}
						limitSubmissions={limitSubmissions}
						updateCallback={this.toggleSubmissionsLimit}
						localization={localization}
					/>
					<CloseDateSection
						schedule={closeSchedule}
						hasScheduleClosing={hasScheduleClosing}
						addDateCallback={this.addDate}
						timeInterval={timeInterval}
						minDate={minCloseDate}
						removeDateCallback={this.removeDate}
						localization={localization}
					/>
					{closeMessage}
				</div>
			</section>
		);
	}

	toggleSubmissionsLimit = (property, value) => {
		this.setState({limitSubmissions: value});
		this.updateSetting(property, value);
	};

	hasScheduleObject = () => {
		const { formData } = this.props;
		return formData.settings.hasOwnProperty(FormSettings.SCHEDULE);
	};

	// Save and show the new date.
	addDate = (key, date) => {
		const { formData } = this.props;

		const closingDate = this.getDate(FormSettings.CLOSE_SCHEDULE);
		const openingDate = this.getDate(FormSettings.OPEN_SCHEDULE);
		const newSchedule = coalesce(formData.settings, FormSettings.SCHEDULE, {});
		newSchedule[key] = date;

		if (key === FormSettings.OPEN_SCHEDULE) {
			this.updateSetting(FormSettings.SCHEDULE, newSchedule);
			// Avoid any past closingDate
			if (closingDate) {
				const minCloseDate = this.getMinCloseDate();
				const currentCloseDate = closingDate || getUtcString();
				this.setState({
					openSchedule: date,
					hasScheduleOpening: true,
					minCloseDate,
					closeSchedule: currentCloseDate < minCloseDate ? minCloseDate : currentCloseDate
				});
			} else {
				this.setState({
					openSchedule: date,
					hasScheduleOpening: true
				});
			}
		} else if (key === FormSettings.CLOSE_SCHEDULE) {
			// Update closeSchedule when the new closing date is in the future
			// of the opening date, or there wasn't a closing date at all.
			if (!openingDate || (openingDate && date > openingDate) ||
				!closingDate) {
				const minCloseDate = this.getMinCloseDate();
				this.updateSetting(FormSettings.SCHEDULE, newSchedule);
				this.setState({
					closeSchedule: date < minCloseDate ? minCloseDate : date,
					hasScheduleClosing: true,
					minCloseDate
				});
			}
		}
	};

	/*
	* Get date from form data, if it doesn't exist then return null.
	* param key: either FormSettings.OPEN_SCHEDULE or FormSettings.CLOSE_SCHEDULE
 	*/
	getDate = (key) => {
		const { formData } = this.props;

		let hasDate = false;
		if (formData.settings[FormSettings.SCHEDULE]) {
			hasDate = formData.settings[FormSettings.SCHEDULE].hasOwnProperty(key);
			if (hasDate) {
				return formData.settings[FormSettings.SCHEDULE][key];
			}
		}
		return null;
	};

	/*
	* This method returns a lower limit for the closing dates. You usually
	* must update the form data first, and then use this.
	*/
	getMinCloseDate = () => {
		const openDate = this.getDate(FormSettings.OPEN_SCHEDULE);
		// Only if we have a openDate we should set a minClosingDate
		// in the future of the opening date.
		if (openDate) {
			return openDate + timeIntervalMillisecs;
		} else {
			return null;
		}
	};

	removeDate = (key) => {
		// Stop remove process if there is nothing to remove.
		const missingOpenDate = key === FormSettings.OPEN_SCHEDULE &&
			!this.getDate(FormSettings.OPEN_SCHEDULE);
		const missingCloseDate = key === FormSettings.CLOSE_SCHEDULE &&
			!this.getDate(FormSettings.CLOSE_SCHEDULE);
		if (!this.hasScheduleObject() || missingOpenDate || missingCloseDate) {
			return;
		}

		// For each case, hide UI elements
		if (key === FormSettings.OPEN_SCHEDULE) {
			// No more minCloseDate: since there is no more a opening date,
			// the user can set whatever date they want.
			this.setState({
				hasScheduleOpening: false,
				minCloseDate: null
			});
		} else if (key === FormSettings.CLOSE_SCHEDULE) {
			this.setState({hasScheduleClosing: false});
		}

		let newSchedule = {...this.props.formData.settings[FormSettings.SCHEDULE]};
		delete newSchedule[key];

		// If there are not any dates, remove ScheduleObject
		if (!Object.keys(newSchedule).length) {
			this.removeScheduleObject();
		} else {
			this.updateSetting(FormSettings.SCHEDULE, newSchedule);
		}
	}
}



// WEBPACK FOOTER //
// ./src/main/webapps/features/form-builder/react/advanced-settings/components/tabs/scheduling.js